import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { message } from 'antd';
import { loadStripe } from '@stripe/stripe-js';
import { Elements, CardElement, useStripe, useElements } from '@stripe/react-stripe-js';
import FeatherIcon from 'feather-icons-react/build/FeatherIcon';
import Header from '../Header';
import Sidebar from '../Sidebar';
import teleChatApi from '../../api/telechat.api';
import doctorApi from '../../api/doctor.api';
import { Card, EmptyState } from '../../ui';

// File upload constants
const ALLOWED_EXTENSIONS = ['jpg', 'jpeg', 'png', 'gif', 'webp', 'pdf', 'dcm', 'doc', 'docx'];
const MAX_FILE_SIZE = 100 * 1024 * 1024; // 100 MB
const MAX_FILES = 5;
const MAX_COMPLAINT = 2000;

const STEPS = ['Лекар', 'Въпросник', 'Оплакване', 'Плащане', 'Готово'];
const FILE_ICON = { image: 'image', pdf: 'file-text', doc: 'file-text', generic: 'file' };

// Stripe CardElement styling (iframe can't read CSS vars — hex aligned to tokens)
const cardElementOptions = {
  style: {
    base: {
      fontSize: '14px',
      color: '#1f2330',
      fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace',
      '::placeholder': { color: '#94a0b3' },
    },
    invalid: { color: '#c92a2a', iconColor: '#c92a2a' },
  },
  hidePostalCode: true,
  disableLink: true,
};

// Wizard styling — implements the "New Consultation Wizard" design (varvara-ui
// tokens + primitives). Indigo header + stepper · body · footer nav.
const WIZARD_CSS = `
  .wz-outer { display: flex; justify-content: center; align-items: stretch; min-height: calc(100dvh - 70px); padding: var(--vc-space-4); }
  .wz-surface {
    width: 100%; max-width: 760px; display: flex;
    border-radius: var(--vc-radius-xl); overflow: hidden;
    box-shadow: var(--vc-shadow-lg); border: 1px solid var(--vc-color-border); background: var(--vc-color-surface);
  }
  .wz-surface .wz { flex: 1; }
  @media (max-width: 768px) { .wz-outer { padding: 0; } .wz-surface { border-radius: 0; border: none; box-shadow: none; } }

  .wz { font-family: var(--vc-font-sans); color: var(--vc-color-text); display: flex; flex-direction: column; min-height: 100%; background: var(--vc-color-bg); }
  .vc-ico { display: inline-flex; vertical-align: middle; flex: none; }

  /* ---- header + stepper ---- */
  .wz-head { flex: none; background: var(--vc-color-primary); color: #fff; padding: var(--vc-space-5); }

  .stepper { display: flex; align-items: flex-start; }
  .step { flex: 1 1 0; display: flex; flex-direction: column; align-items: center; gap: 7px; position: relative; min-width: 0; }
  .step-line { position: absolute; top: 15px; left: -50%; width: 100%; height: 2px; background: rgba(255,255,255,.28); z-index: 0; }
  .step.done .step-line, .step.current .step-line { background: #fff; }
  .step-dot { position: relative; z-index: 1; width: 32px; height: 32px; border-radius: var(--vc-radius-pill); display: grid; place-items: center; font-family: var(--vc-font-mono); font-size: var(--vc-text-sm); font-weight: var(--vc-weight-bold); border: 2px solid rgba(255,255,255,.5); color: rgba(255,255,255,.85); background: var(--vc-color-primary); }
  .step.done .step-dot { background: #fff; color: var(--vc-color-primary); border-color: #fff; }
  .step.current .step-dot { background: #fff; color: var(--vc-color-primary); border-color: #fff; box-shadow: 0 0 0 4px rgba(255,255,255,.22); }
  .step-name { font-size: var(--vc-text-xs); font-weight: var(--vc-weight-medium); color: rgba(255,255,255,.72); text-align: center; line-height: 1.2; letter-spacing: .01em; }
  .step.current .step-name, .step.done .step-name { color: #fff; }
  @media (max-width: 640px) {
    .step-name { display: none; }
    .step.current .step-name { display: block; position: absolute; top: 38px; white-space: nowrap; }
    .wz-head { padding-bottom: var(--vc-space-6); }
  }

  /* ---- body ---- */
  .wz-body { flex: 1 1 auto; padding: var(--vc-space-5); display: flex; flex-direction: column; gap: var(--vc-space-4); }
  .wz-steptitle { font-size: var(--vc-text-lg); font-weight: var(--vc-weight-semibold); margin: 0; }
  .wz-intro { font-size: var(--vc-text-sm); color: var(--vc-color-text-muted); line-height: var(--vc-leading-normal); margin: -6px 0 0; }
  .req { color: var(--vc-color-danger); }

  /* ---- footer nav ---- */
  .wz-foot { flex: none; display: flex; align-items: center; justify-content: space-between; gap: var(--vc-space-3); padding: var(--vc-space-4) var(--vc-space-5); border-top: 1px solid var(--vc-color-border); background: var(--vc-color-surface); }
  .btn { appearance: none; cursor: pointer; display: inline-flex; align-items: center; gap: 7px; font-family: inherit; font-size: var(--vc-text-base); font-weight: var(--vc-weight-semibold); border-radius: var(--vc-radius-md); padding: var(--vc-space-2) var(--vc-space-4); border: 1px solid transparent; transition: all .12s ease; white-space: nowrap; }
  .btn-primary { background: var(--vc-color-primary); color: #fff; box-shadow: var(--vc-shadow-sm); }
  .btn-primary:hover { filter: brightness(1.08); }
  .btn-primary:disabled { opacity: .5; cursor: not-allowed; filter: none; }
  .btn-ghost { background: transparent; color: var(--vc-color-text-muted); border-color: var(--vc-color-border); }
  .btn-ghost:hover { border-color: var(--vc-color-text-subtle); color: var(--vc-color-text); }
  .btn-ghost:disabled { opacity: .5; cursor: not-allowed; }
  .btn-lg { padding: var(--vc-space-3) var(--vc-space-5); font-size: var(--vc-text-md); }
  .btn-block { width: 100%; justify-content: center; }

  /* ---- search field ---- */
  .field-search { position: relative; display: flex; align-items: center; }
  .field-search .vc-ico { position: absolute; left: var(--vc-space-3); color: var(--vc-color-text-subtle); pointer-events: none; }
  .field-search input { width: 100%; font-family: inherit; font-size: var(--vc-text-base); color: var(--vc-color-text); padding: 10px var(--vc-space-3) 10px calc(var(--vc-space-3)*2 + 16px); border: 1px solid var(--vc-color-border); border-radius: var(--vc-radius-md); background: var(--vc-color-surface); outline: none; }
  .field-search input::placeholder { color: var(--vc-color-text-subtle); }
  .field-search input:focus { border-color: var(--vc-color-primary); box-shadow: 0 0 0 3px var(--vc-color-primary-soft); }

  /* ---- doctor grid ---- */
  .doc-grid { display: grid; grid-template-columns: repeat(auto-fill, minmax(220px, 1fr)); gap: var(--vc-space-3); }
  @media (max-width: 640px) { .doc-grid { grid-template-columns: 1fr; } }
  .doc-card { position: relative; cursor: pointer; display: flex; align-items: center; gap: var(--vc-space-3); padding: var(--vc-space-3); background: var(--vc-color-surface); border: 1.5px solid var(--vc-color-border); border-radius: var(--vc-radius-lg); transition: all .12s ease; text-align: left; width: 100%; }
  .doc-card:hover { border-color: var(--vc-color-primary); box-shadow: var(--vc-shadow-sm); }
  .doc-card.sel { border-color: var(--vc-color-primary); background: var(--vc-color-primary-soft); box-shadow: 0 0 0 1px var(--vc-color-primary); }
  .doc-av { flex: none; width: 46px; height: 46px; border-radius: var(--vc-radius-pill); background: var(--vc-color-primary); color: #fff; display: grid; place-items: center; font-weight: var(--vc-weight-bold); font-size: var(--vc-text-md); }
  .doc-meta { min-width: 0; flex: 1 1 auto; display: flex; flex-direction: column; gap: 2px; }
  .doc-name { font-size: var(--vc-text-md); font-weight: var(--vc-weight-semibold); color: var(--vc-color-text); line-height: var(--vc-leading-tight); }
  .doc-spec { font-size: var(--vc-text-sm); color: var(--vc-color-primary); font-weight: var(--vc-weight-medium); }
  .doc-dept { font-size: var(--vc-text-xs); color: var(--vc-color-text-muted); }
  .doc-check { position: absolute; top: 10px; right: 10px; width: 22px; height: 22px; border-radius: var(--vc-radius-pill); background: var(--vc-color-primary); color: #fff; display: grid; place-items: center; }

  /* ---- questionnaire ---- */
  .q-item { display: flex; flex-direction: column; gap: var(--vc-space-2); }
  .q-label { display: flex; align-items: flex-start; gap: var(--vc-space-2); font-size: var(--vc-text-md); font-weight: var(--vc-weight-medium); color: var(--vc-color-text); }
  .q-num { flex: none; width: 24px; height: 24px; border-radius: var(--vc-radius-pill); background: var(--vc-color-primary-soft); color: var(--vc-color-primary); display: grid; place-items: center; font-family: var(--vc-font-mono); font-size: var(--vc-text-xs); font-weight: var(--vc-weight-bold); margin-top: 1px; }
  .ta { width: 100%; resize: vertical; font-family: inherit; font-size: var(--vc-text-base); color: var(--vc-color-text); line-height: var(--vc-leading-normal); padding: var(--vc-space-3); border: 1px solid var(--vc-color-border); border-radius: var(--vc-radius-md); background: var(--vc-color-surface); outline: none; }
  .ta::placeholder { color: var(--vc-color-text-subtle); }
  .ta:focus { border-color: var(--vc-color-primary); box-shadow: 0 0 0 3px var(--vc-color-primary-soft); }

  /* ---- complaint + files ---- */
  .field-head { display: flex; align-items: baseline; justify-content: space-between; gap: var(--vc-space-3); }
  .field-lbl { font-size: var(--vc-text-md); font-weight: var(--vc-weight-semibold); }
  .char-count { font-family: var(--vc-font-mono); font-size: var(--vc-text-xs); color: var(--vc-color-text-subtle); }
  .dropzone { display: flex; flex-direction: column; align-items: center; gap: var(--vc-space-2); padding: var(--vc-space-5); border: 1.5px dashed var(--vc-color-border); border-radius: var(--vc-radius-lg); background: var(--vc-color-surface-muted); color: var(--vc-color-text-muted); cursor: pointer; text-align: center; transition: all .12s ease; }
  .dropzone:hover { border-color: var(--vc-color-primary); color: var(--vc-color-primary); background: var(--vc-color-primary-soft); }
  .dz-title { font-size: var(--vc-text-md); font-weight: var(--vc-weight-semibold); color: var(--vc-color-text); }
  .dz-hint { font-size: var(--vc-text-xs); }
  .file-list { display: flex; flex-direction: column; gap: var(--vc-space-2); margin-top: var(--vc-space-3); }
  .file-chip { display: flex; align-items: center; gap: var(--vc-space-3); padding: var(--vc-space-2) var(--vc-space-3); border-radius: var(--vc-radius-md); background: var(--vc-color-surface); border: 1px solid var(--vc-color-border); }
  .file-ico { flex: none; width: 34px; height: 34px; border-radius: var(--vc-radius-md); display: grid; place-items: center; color: #fff; }
  .file-ico.image { background: #7048e8; } .file-ico.pdf { background: #c92a2a; } .file-ico.doc { background: #1971c2; } .file-ico.generic { background: var(--vc-color-neutral); }
  .file-meta { min-width: 0; flex: 1 1 auto; display: flex; flex-direction: column; gap: 1px; }
  .file-name { font-size: var(--vc-text-sm); font-weight: var(--vc-weight-medium); white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
  .file-size { font-family: var(--vc-font-mono); font-size: var(--vc-text-xs); color: var(--vc-color-text-subtle); }
  .file-rm { appearance: none; cursor: pointer; flex: none; width: 28px; height: 28px; border-radius: var(--vc-radius-pill); border: none; color: var(--vc-color-text-muted); background: var(--vc-color-bg); display: grid; place-items: center; }
  .file-rm:hover { color: var(--vc-color-danger); background: var(--vc-color-danger-bg); }

  /* ---- summary card ---- */
  .sum-row { display: flex; align-items: center; gap: var(--vc-space-3); }
  .sum-qa { display: flex; flex-direction: column; gap: var(--vc-space-3); }
  .sum-q { font-size: var(--vc-text-sm); font-weight: var(--vc-weight-semibold); color: var(--vc-color-text); }
  .sum-a { font-size: var(--vc-text-sm); color: var(--vc-color-text-muted); line-height: var(--vc-leading-normal); margin-top: 2px; }
  .divider { height: 1px; background: var(--vc-color-border-soft); margin: var(--vc-space-1) 0; }

  /* ---- payment ---- */
  .pay-amount { display: flex; align-items: baseline; justify-content: space-between; gap: var(--vc-space-3); }
  .pay-amount .lbl { font-size: var(--vc-text-md); color: var(--vc-color-text-muted); }
  .pay-amount .val { font-family: var(--vc-font-mono); font-size: var(--vc-text-3xl); font-weight: var(--vc-weight-bold); color: var(--vc-color-text); }
  .pay-amount .was { font-family: var(--vc-font-mono); font-size: var(--vc-text-md); color: var(--vc-color-text-subtle); text-decoration: line-through; margin-right: var(--vc-space-2); }
  .stripe-el { display: flex; align-items: center; gap: var(--vc-space-3); padding: 12px var(--vc-space-3); border: 1px solid var(--vc-color-border); border-radius: var(--vc-radius-md); background: var(--vc-color-surface); }
  .stripe-el:focus-within { border-color: var(--vc-color-primary); box-shadow: 0 0 0 3px var(--vc-color-primary-soft); }
  .stripe-el .StripeElement { flex: 1 1 auto; min-width: 0; }
  .field-lbl-sm { font-size: var(--vc-text-sm); font-weight: var(--vc-weight-medium); color: var(--vc-color-text-muted); margin-bottom: 6px; display: block; }
  .note { display: flex; align-items: flex-start; gap: var(--vc-space-2); border-radius: var(--vc-radius-md); padding: var(--vc-space-3); font-size: var(--vc-text-sm); line-height: var(--vc-leading-normal); }
  .note .n-ico { flex: none; margin-top: 1px; }
  .note.info { background: var(--vc-color-info-bg); color: var(--vc-color-info); }
  .note.success { background: var(--vc-color-success-bg); color: var(--vc-color-success); font-weight: var(--vc-weight-medium); }
  .note.info .n-txt { color: var(--vc-color-text-soft); }

  /* ---- success ---- */
  .done-wrap { display: flex; flex-direction: column; align-items: center; text-align: center; gap: var(--vc-space-4); padding: var(--vc-space-8) var(--vc-space-5); }
  .done-badge { width: 84px; height: 84px; border-radius: var(--vc-radius-pill); background: var(--vc-color-success-bg); color: var(--vc-color-success); display: grid; place-items: center; box-shadow: 0 0 0 8px color-mix(in srgb, var(--vc-color-success) 8%, transparent); animation: wz-pop .5s cubic-bezier(.2,1.3,.5,1) both; }
  @keyframes wz-pop { 0% { transform: scale(.5); opacity: 0; } 100% { transform: scale(1); opacity: 1; } }
  .done-title { font-size: var(--vc-text-xl); font-weight: var(--vc-weight-semibold); margin: 0; }
  .done-desc { font-size: var(--vc-text-md); color: var(--vc-color-text-muted); line-height: var(--vc-leading-normal); max-width: 420px; margin: 0; }

  /* ---- skeleton ---- */
  .sk { background: linear-gradient(90deg, var(--vc-color-border-soft) 25%, var(--vc-color-surface-muted) 50%, var(--vc-color-border-soft) 75%); background-size: 200% 100%; animation: wz-sk 1.3s ease infinite; border-radius: var(--vc-radius-sm); }
  @keyframes wz-sk { 0% { background-position: 200% 0; } 100% { background-position: -200% 0; } }
`;

// --- helpers ---
const eur = (n) => Number(n || 0).toFixed(2) + ' €';

const docInitial = (name) => {
  if (!name) return 'Д';
  const clean = name.replace(/^Д-?р\s+/i, '').trim();
  return (clean[0] || name[0] || '?').toUpperCase();
};

const formatFileSize = (bytes) => {
  if (bytes < 1024) return bytes + ' B';
  if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
  return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
};

const fileKindFromName = (name) => {
  const ext = name.split('.').pop()?.toLowerCase();
  if (['jpg', 'jpeg', 'png', 'gif', 'webp'].includes(ext)) return 'image';
  if (ext === 'pdf') return 'pdf';
  if (['doc', 'docx'].includes(ext)) return 'doc';
  return 'generic';
};

// Payment Form (must live inside the Elements provider)
/* eslint-disable react/prop-types */
const PaymentForm = ({
  clientSecret,
  onPaymentSuccess,
  onPaymentError,
  submitting,
  setSubmitting,
  consultationPrice,
  isFollowUp,
  discountPercent,
}) => {
  const stripe = useStripe();
  const elements = useElements();
  const [cardError, setCardError] = useState(null);
  const [cardComplete, setCardComplete] = useState(false);

  const hasDiscount = isFollowUp && discountPercent > 0;
  const price = Number(consultationPrice || 0);
  const original = hasDiscount && discountPercent < 100 ? price / (1 - discountPercent / 100) : null;

  const handleCardChange = (event) => {
    setCardError(event.error ? event.error.message : null);
    setCardComplete(event.complete);
  };

  const handleSubmit = async () => {
    if (!stripe || !elements) return;
    if (!cardComplete) {
      setCardError('Моля въведете данните на картата');
      return;
    }
    setSubmitting(true);
    setCardError(null);
    try {
      const cardElement = elements.getElement(CardElement);
      const { error, paymentIntent } = await stripe.confirmCardPayment(clientSecret, {
        payment_method: { card: cardElement },
      });
      if (error) {
        setCardError(error.message);
        onPaymentError(error.message);
        setSubmitting(false);
      } else if (paymentIntent) {
        // requires_capture (manual capture) or otherwise — parent finalizes
        onPaymentSuccess(paymentIntent.id);
      }
    } catch (err) {
      console.error('Payment error:', err);
      setCardError('Възникна грешка при обработка на плащането');
      onPaymentError(err.message);
      setSubmitting(false);
    }
  };

  return (
    <>
      <h2 className="wz-steptitle">Плащане</h2>

      <Card flat>
        <div className="pay-amount">
          <span className="lbl">Сума за плащане</span>
          <span>
            {original && <span className="was">{eur(original)}</span>}
            <span className="val">{eur(price)}</span>
          </span>
        </div>
      </Card>

      {hasDiscount && (
        <div className="note success">
          <span className="n-ico"><FeatherIcon icon="tag" size={16} /></span>
          <span>Имате приключена консултация с този лекар в последните 30 дни. Приложена е {discountPercent}% отстъпка за последваща консултация.</span>
        </div>
      )}

      <div>
        <span className="field-lbl-sm">Данни на картата</span>
        <div className="stripe-el">
          <CardElement options={cardElementOptions} onChange={handleCardChange} />
        </div>
        {cardError && (
          <div style={{ color: 'var(--vc-color-danger)', fontSize: 'var(--vc-text-sm)', marginTop: 'var(--vc-space-2)' }}>{cardError}</div>
        )}
      </div>

      <div className="note info">
        <span className="n-ico"><FeatherIcon icon="info" size={16} /></span>
        <span className="n-txt">След одобрение от лекаря сумата ще бъде таксувана от картата ви. Ако лекарят откаже консултацията, сумата ще бъде възстановена автоматично.</span>
      </div>

      <button className="btn btn-primary btn-lg btn-block" onClick={handleSubmit} disabled={!stripe || !cardComplete || submitting}>
        <FeatherIcon icon="lock" size={16} /> {submitting ? 'Създаване на консултация...' : `Плати ${eur(price)}`}
      </button>
    </>
  );
};
/* eslint-enable react/prop-types */

const NewConsultation = () => {
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  // Stripe
  const [stripePromise, setStripePromise] = useState(null);
  const [stripeLoading, setStripeLoading] = useState(true);

  // Step 1: Doctor selection
  const [doctors, setDoctors] = useState([]);
  const [selectedDoctor, setSelectedDoctor] = useState(null);
  const [doctorSearch, setDoctorSearch] = useState('');

  // Step 2: Pre-screening questions
  const [questions, setQuestions] = useState([]);
  const [answers, setAnswers] = useState({});

  // Step 3: Main complaint + files
  const [mainComplaint, setMainComplaint] = useState('');
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);

  // Step 4: Payment
  const [clientSecret, setClientSecret] = useState(null);
  const [consultationPrice, setConsultationPrice] = useState(null);
  const [isFollowUp, setIsFollowUp] = useState(false);
  const [discountPercent, setDiscountPercent] = useState(null);

  // Step 5: Success
  const [chatId, setChatId] = useState(null);

  useEffect(() => {
    loadDoctors();
    loadQuestions();
    loadStripeConfig();
  }, []);

  // Scroll to top when step changes (fixes iOS rendering issue)
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [currentStep]);

  const loadStripeConfig = async () => {
    try {
      setStripeLoading(true);
      const config = await teleChatApi.getStripeConfig();
      const stripe = await loadStripe(config.publishableKey);
      setStripePromise(stripe);
    } catch (error) {
      console.error('Error loading Stripe config:', error);
      message.error('Грешка при зареждане на платежната система');
    } finally {
      setStripeLoading(false);
    }
  };

  const loadDoctors = async () => {
    try {
      setLoading(true);
      const data = await doctorApi.getAllDoctors(true); // active only
      setDoctors(data);
    } catch (error) {
      console.error('Error loading doctors:', error);
      message.error('Грешка при зареждане на лекарите');
    } finally {
      setLoading(false);
    }
  };

  const loadQuestions = async () => {
    try {
      const data = await teleChatApi.getPreScreeningQuestions();
      setQuestions(data);
      const initialAnswers = {};
      data.forEach((q) => { initialAnswers[q.questionId] = ''; });
      setAnswers(initialAnswers);
    } catch (error) {
      console.error('Error loading questions:', error);
      message.error('Грешка при зареждане на въпросника');
    }
  };

  const filteredDoctors = doctors.filter((doctor) => {
    if (!doctorSearch) return true;
    const search = doctorSearch.toLowerCase();
    return (
      doctor.fullName?.toLowerCase().includes(search) ||
      doctor.speciality?.toLowerCase().includes(search) ||
      doctor.department?.name?.toLowerCase().includes(search)
    );
  });

  const handleDoctorSelect = (doctor) => setSelectedDoctor(doctor);
  const handleAnswerChange = (questionId, value) => setAnswers((prev) => ({ ...prev, [questionId]: value }));

  const handleFileSelect = (e) => {
    const files = Array.from(e.target.files || []);
    for (const file of files) {
      if (selectedFiles.length >= MAX_FILES) {
        message.warning(`Максимум ${MAX_FILES} файла са позволени`);
        break;
      }
      const ext = file.name.split('.').pop()?.toLowerCase();
      if (!ALLOWED_EXTENSIONS.includes(ext)) {
        message.error(`Невалиден тип файл: ${file.name}. Позволени: ${ALLOWED_EXTENSIONS.join(', ')}`);
        continue;
      }
      if (file.size > MAX_FILE_SIZE) {
        message.error(`Файлът ${file.name} е твърде голям. Максимум 100 MB`);
        continue;
      }
      if (selectedFiles.some((f) => f.name === file.name && f.size === file.size)) {
        message.warning(`Файлът ${file.name} вече е добавен`);
        continue;
      }
      setSelectedFiles((prev) => [...prev, file]);
    }
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleRemoveFile = (index) => setSelectedFiles((prev) => prev.filter((_, i) => i !== index));

  const validateStep = () => {
    if (currentStep === 0) {
      if (!selectedDoctor) {
        message.warning('Моля изберете лекар');
        return false;
      }
    } else if (currentStep === 2) {
      if (!mainComplaint.trim()) {
        message.warning('Моля опишете основното си оплакване');
        return false;
      }
    }
    // Step 1 (pre-screening) answers are optional per the design copy.
    return true;
  };

  const handleNext = async () => {
    if (!validateStep()) return;
    if (currentStep === 2) {
      await preparePaymentRequest();
    } else {
      setCurrentStep((prev) => prev + 1);
    }
  };

  const handlePrev = () => setCurrentStep((prev) => prev - 1);

  const preparePaymentRequest = async () => {
    try {
      setSubmitting(true);
      const preScreeningAnswers = questions.map((q) => ({ questionId: q.questionId, answerText: answers[q.questionId] }));
      const response = await teleChatApi.preparePayment({
        doctorId: selectedDoctor.id,
        preScreeningAnswers,
        mainComplaint: mainComplaint.trim(),
      });
      setClientSecret(response.clientSecret);
      setConsultationPrice(response.consultationPrice);
      setIsFollowUp(response.followUp);
      setDiscountPercent(response.discountPercent);
      setCurrentStep(3);
    } catch (error) {
      console.error('Error preparing payment:', error);
      message.error(error?.data?.message || 'Грешка при подготовка на плащането');
    } finally {
      setSubmitting(false);
    }
  };

  const handlePaymentSuccess = async (confirmedPaymentIntentId) => {
    try {
      setSubmitting(true);
      const preScreeningAnswers = questions.map((q) => ({ questionId: q.questionId, answerText: answers[q.questionId] }));
      const response = await teleChatApi.createChat({
        doctorId: selectedDoctor.id,
        preScreeningAnswers,
        mainComplaint: mainComplaint.trim(),
        paymentIntentId: confirmedPaymentIntentId,
      });
      setChatId(response.chatId);

      // Upload files now that we have a chatId
      if (selectedFiles.length > 0) {
        setUploading(true);
        try {
          for (const file of selectedFiles) {
            const fileData = await teleChatApi.uploadFile(response.chatId, file, false);
            await teleChatApi.sendPatientMessage(response.chatId, {
              fileKey: fileData.fileKey,
              fileName: fileData.fileName,
              fileType: fileData.fileType,
              fileSizeBytes: fileData.fileSizeBytes,
            });
          }
        } catch (uploadError) {
          console.error('Error uploading files:', uploadError);
          message.warning('Някои файлове не бяха качени успешно. Можете да ги добавите по-късно в чата.');
        } finally {
          setUploading(false);
        }
      }

      message.success('Консултацията е създадена успешно!');
      setCurrentStep(4);
    } catch (error) {
      console.error('Error creating consultation:', error);
      message.error(error?.data?.message || 'Грешка при създаване на консултацията');
    } finally {
      setSubmitting(false);
    }
  };

  const handlePaymentError = (errorMessage) => {
    message.error(errorMessage || 'Грешка при плащането');
  };

  const goToChat = () => navigate(`/telechat/${chatId}`);

  const getNextText = () => {
    if (currentStep === 2) {
      if (uploading) return 'Качване на файлове...';
      if (submitting) return 'Създаване...';
      return 'Напред към плащане';
    }
    return 'Напред';
  };

  // --- step renderers ---
  const renderStepper = () => (
    <div className="stepper">
      {STEPS.map((name, i) => {
        const state = i < currentStep ? 'done' : i === currentStep ? 'current' : 'upcoming';
        return (
          <div className={'step ' + state} key={name}>
            {i > 0 && <span className="step-line" />}
            <span className="step-dot">{i < currentStep ? <FeatherIcon icon="check" size={15} /> : i + 1}</span>
            <span className="step-name">{name}</span>
          </div>
        );
      })}
    </div>
  );

  const renderStep1 = () => (
    <>
      <h2 className="wz-steptitle">Изберете лекар</h2>
      <div className="field-search">
        <span className="vc-ico"><FeatherIcon icon="search" size={16} /></span>
        <input
          type="search"
          placeholder="Търси по име, специалност или отделение..."
          value={doctorSearch}
          onChange={(e) => setDoctorSearch(e.target.value)}
        />
      </div>
      {loading ? (
        <div className="doc-grid">
          {Array.from({ length: 4 }).map((_, i) => <span key={i} className="sk" style={{ height: 74, borderRadius: 'var(--vc-radius-lg)' }} />)}
        </div>
      ) : filteredDoctors.length === 0 ? (
        <Card>
          <EmptyState icon={<FeatherIcon icon="user" size={26} />} title="Няма намерени лекари" description="Опитайте с друго име, специалност или отделение." />
        </Card>
      ) : (
        <div className="doc-grid">
          {filteredDoctors.map((d) => (
            <button key={d.id} type="button" className={'doc-card' + (selectedDoctor?.id === d.id ? ' sel' : '')} onClick={() => handleDoctorSelect(d)}>
              <span className="doc-av">{docInitial(d.fullName)}</span>
              <span className="doc-meta">
                <span className="doc-name">{d.fullName}</span>
                <span className="doc-spec">{d.speciality}</span>
                {d.department?.name && <span className="doc-dept">{d.department.name}</span>}
              </span>
              {selectedDoctor?.id === d.id && <span className="doc-check"><FeatherIcon icon="check" size={14} /></span>}
            </button>
          ))}
        </div>
      )}
    </>
  );

  const renderStep2 = () => (
    <>
      <h2 className="wz-steptitle">Предварителен въпросник</h2>
      <p className="wz-intro">Отговорите ще помогнат на лекаря да прегледа случая Ви по-бързо. Можете да оставите поле празно, ако не се отнася за Вас.</p>
      {questions.length === 0 ? (
        <Card>
          <EmptyState icon={<FeatherIcon icon="help-circle" size={26} />} title="Няма въпроси" description="Този лекар не изисква предварителен въпросник. Продължете към описание на оплакването." />
        </Card>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--vc-space-4)' }}>
          {questions.map((q, i) => (
            <div className="q-item" key={q.questionId}>
              <label className="q-label"><span className="q-num">{i + 1}</span>{q.questionText}</label>
              <textarea
                className="ta"
                rows={2}
                placeholder="Вашият отговор..."
                value={answers[q.questionId] || ''}
                onChange={(e) => handleAnswerChange(q.questionId, e.target.value)}
                maxLength={1000}
              />
            </div>
          ))}
        </div>
      )}
    </>
  );

  const renderStep3 = () => {
    const answered = questions.filter((q) => answers[q.questionId]?.trim());
    return (
      <>
        <h2 className="wz-steptitle">Опишете оплакването си</h2>
        <div>
          <div className="field-head">
            <span className="field-lbl">Основно оплакване <span className="req">*</span></span>
            <span className="char-count">{mainComplaint.length} / {MAX_COMPLAINT}</span>
          </div>
          <textarea
            className="ta"
            rows={5}
            maxLength={MAX_COMPLAINT}
            placeholder="Опишете подробно оплакването си, кога започна и как се променя..."
            value={mainComplaint}
            onChange={(e) => setMainComplaint(e.target.value)}
            style={{ marginTop: 'var(--vc-space-2)' }}
          />
        </div>

        <div>
          <span className="field-lbl">Прикачени файлове</span>
          <div className="dropzone" onClick={() => fileInputRef.current?.click()} style={{ marginTop: 'var(--vc-space-2)' }}>
            <FeatherIcon icon="upload-cloud" size={26} />
            <span className="dz-title">Добави файл</span>
            <span className="dz-hint">Снимки, PDF или документи · до {MAX_FILES} файла</span>
          </div>
          <input
            type="file"
            ref={fileInputRef}
            onChange={handleFileSelect}
            multiple
            accept={ALLOWED_EXTENSIONS.map((ext) => `.${ext}`).join(',')}
            style={{ display: 'none' }}
          />
          {selectedFiles.length > 0 && (
            <div className="file-list">
              {selectedFiles.map((file, i) => {
                const kind = fileKindFromName(file.name);
                return (
                  <div className="file-chip" key={`${file.name}-${i}`}>
                    <span className={'file-ico ' + kind}><FeatherIcon icon={FILE_ICON[kind]} size={17} /></span>
                    <div className="file-meta">
                      <span className="file-name">{file.name}</span>
                      <span className="file-size">{formatFileSize(file.size)}</span>
                    </div>
                    <button className="file-rm" aria-label="Премахни" onClick={() => handleRemoveFile(i)}><FeatherIcon icon="x" size={15} /></button>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        <Card flat>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--vc-space-3)' }}>
            <span className="field-lbl">Обобщение на консултацията</span>
            <div className="sum-row">
              <span className="doc-av">{docInitial(selectedDoctor?.fullName)}</span>
              <div className="doc-meta">
                <span className="doc-name">{selectedDoctor?.fullName}</span>
                <span className="doc-spec">{selectedDoctor?.speciality}</span>
              </div>
            </div>
            {answered.length > 0 && <div className="divider" />}
            <div className="sum-qa">
              {answered.map((q) => (
                <div key={q.questionId}>
                  <div className="sum-q">{q.questionText}</div>
                  <div className="sum-a">{answers[q.questionId]}</div>
                </div>
              ))}
            </div>
          </div>
        </Card>
      </>
    );
  };

  const renderPayment = () => {
    const hasDiscount = isFollowUp && discountPercent > 0;
    const price = Number(consultationPrice || 0);
    const original = hasDiscount && discountPercent < 100 ? price / (1 - discountPercent / 100) : null;

    if (stripeLoading || !stripePromise) {
      // Loading variant — real amount, skeleton card element
      return (
        <>
          <h2 className="wz-steptitle">Плащане</h2>
          <Card flat>
            <div className="pay-amount">
              <span className="lbl">Сума за плащане</span>
              <span>
                {original && <span className="was">{eur(original)}</span>}
                <span className="val">{eur(price)}</span>
              </span>
            </div>
          </Card>
          {hasDiscount && (
            <div className="note success">
              <span className="n-ico"><FeatherIcon icon="tag" size={16} /></span>
              <span>Приложена е {discountPercent}% отстъпка за последваща консултация.</span>
            </div>
          )}
          <div>
            <span className="field-lbl-sm">Данни на картата</span>
            <div className="stripe-el" style={{ gap: 'var(--vc-space-3)' }}>
              <span className="sk" style={{ height: 18, flex: '1 1 auto', borderRadius: 4 }} />
              <span className="sk" style={{ height: 18, width: 62, borderRadius: 4 }} />
              <span className="sk" style={{ height: 18, width: 48, borderRadius: 4 }} />
            </div>
          </div>
          <div className="note info">
            <span className="n-ico"><FeatherIcon icon="info" size={16} /></span>
            <span className="n-txt">След одобрение от лекаря сумата ще бъде таксувана от картата ви. Ако лекарят откаже консултацията, сумата ще бъде възстановена автоматично.</span>
          </div>
          <button className="btn btn-primary btn-lg btn-block" disabled>
            <FeatherIcon icon="lock" size={16} /> Плати {eur(price)}
          </button>
        </>
      );
    }

    return (
      <Elements stripe={stripePromise} options={{ clientSecret }}>
        <PaymentForm
          clientSecret={clientSecret}
          onPaymentSuccess={handlePaymentSuccess}
          onPaymentError={handlePaymentError}
          submitting={submitting}
          setSubmitting={setSubmitting}
          consultationPrice={consultationPrice}
          isFollowUp={isFollowUp}
          discountPercent={discountPercent}
        />
      </Elements>
    );
  };

  const renderStep5 = () => (
    <div className="done-wrap">
      <span className="done-badge"><FeatherIcon icon="check" size={42} /></span>
      <h2 className="done-title">Консултацията е създадена успешно!</h2>
      <p className="done-desc">
        Заявката беше изпратена до {selectedDoctor?.fullName}. Ще получите известие, когато лекарят я одобри.
        Сумата от <strong>{eur(consultationPrice)}</strong> се таксува от картата ви само след одобрение —
        ако лекарят откаже, нищо няма да бъде удържано.
      </p>
      <button className="btn btn-primary btn-lg" onClick={goToChat}>
        Към консултацията <FeatherIcon icon="arrow-right" size={16} />
      </button>
    </div>
  );

  return (
    <div>
      <Header />
      <Sidebar activeClassName="telechat" />
      <style>{WIZARD_CSS}</style>

      <div className="page-wrapper">
        <div className="content" style={{ padding: 0 }}>
          <div className="wz-outer">
            <div className="wz-surface">
              <div className="wz">
                {/* Header + stepper */}
                <div className="wz-head">
                  {renderStepper()}
                </div>

                {/* Body */}
                <div className="wz-body">
                  {currentStep === 0 && renderStep1()}
                  {currentStep === 1 && renderStep2()}
                  {currentStep === 2 && renderStep3()}
                  {currentStep === 3 && renderPayment()}
                  {currentStep === 4 && renderStep5()}
                </div>

                {/* Footer nav */}
                {currentStep <= 2 && (
                  <div className="wz-foot">
                    <button className="btn btn-ghost" onClick={handlePrev} disabled={currentStep === 0}>
                      <FeatherIcon icon="arrow-left" size={16} /> Назад
                    </button>
                    <button className="btn btn-primary" onClick={handleNext} disabled={submitting || uploading}>
                      {getNextText()} <FeatherIcon icon="arrow-right" size={16} />
                    </button>
                  </div>
                )}
                {currentStep === 3 && (
                  <div className="wz-foot" style={{ justifyContent: 'flex-start' }}>
                    <button className="btn btn-ghost" onClick={handlePrev} disabled={submitting || uploading}>
                      <FeatherIcon icon="arrow-left" size={16} /> Назад
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default NewConsultation;
